// src/pages/CapturePage/CapturePage.js
//
// A capture session: one sitting with a child, on one device.
//
// The examiner sets up, hands the tablet over, the child records, and the
// tablet comes back. That hand-over is why this page has no route anywhere else
// while a block is running: if a child does get out of a block they land on
// their own session screen and no further — never the dashboard, never another
// child's file.
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import FastTalkingGame from './blocks/FastTalkingGame';
import LongSoundGame from './blocks/LongSoundGame';
import MouthGymBlock from './blocks/MouthGymBlock';
import StoryTime from './blocks/StoryTime';
import PreflightCard from './PreflightCard';
import { BLOCK_STATE_LABEL, captureError, completeSession, getProtocol, getSession } from './captureApi';
import './CapturePage.css';

const RUNNERS = {
    ddk: FastTalkingGame,
    vowel: LongSoundGame,
    conversation: StoryTime,
    oral_motor: MouthGymBlock,
};

function monthsBetween(dob) {
    if (!dob) return null;
    const born = new Date(dob);
    if (Number.isNaN(born.getTime())) return null;
    const now = new Date();
    return (now.getFullYear() - born.getFullYear()) * 12 + (now.getMonth() - born.getMonth());
}

export default function CapturePage() {
    const { sessionId } = useParams();
    const navigate = useNavigate();

    const [session, setSession] = useState(null);
    const [protocol, setProtocol] = useState(null);
    const [activeBlock, setActiveBlock] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [closing, setClosing] = useState(false);

    const load = useCallback(async () => {
        setError('');
        try {
            const [s, p] = await Promise.all([getSession(sessionId), getProtocol()]);
            setSession(s.data);
            setProtocol(p.data);
        } catch (e) {
            setError(captureError(e, 'This session could not be opened.'));
        } finally {
            setLoading(false);
        }
    }, [sessionId]);

    useEffect(() => { load(); }, [load]);

    // A child holding a tablet should not be able to walk back into the app by
    // swiping. While a block runs, the back gesture returns to the session hub
    // rather than leaving it.
    useEffect(() => {
        if (!activeBlock) return undefined;
        const onPop = (e) => {
            if (e) e.preventDefault();
            setActiveBlock(null);
            window.history.pushState(null, '', window.location.href);
        };
        window.history.pushState(null, '', window.location.href);
        window.addEventListener('popstate', onPop);
        return () => window.removeEventListener('popstate', onPop);
    }, [activeBlock]);

    const close = async () => {
        setClosing(true);
        try {
            const r = await completeSession(session.id);
            setSession(r.data);
            navigate('/intake');
        } catch (e) {
            setError(captureError(e, 'Could not close the session.'));
            setClosing(false);
        }
    };

    if (loading) return <div className="cap-page"><p>Opening the session…</p></div>;
    if (!session || !protocol) {
        return (
            <div className="cap-page">
                <p className="cap-error">{error || 'This session could not be opened.'}</p>
                <button type="button" className="cap-btn ghost" onClick={() => navigate('/intake')}>
                    Back to Intake
                </button>
            </div>
        );
    }

    const blocks = protocol.blocks || [];
    const ageMonths = monthsBetween(session.child_dob);

    // ── A block is running: the child has the tablet, and this is the only
    //    thing on screen. No navigation, no clinical words, no scores. ──────
    if (activeBlock) {
        const block = blocks.find((b) => b.key === activeBlock);
        const Runner = RUNNERS[activeBlock];
        if (!block || !Runner) { setActiveBlock(null); return null; }
        return (
            <div className="cap-page child-mode">
                <Runner
                    session={session}
                    block={block}
                    ageMonths={ageMonths}
                    onSaved={setSession}
                    onFinish={() => setActiveBlock(null)}
                />
            </div>
        );
    }

    const startHat = () => {
        // The word list is the ordinary child page, told which session it belongs
        // to. It sits under /intake because the examiner is the one logged in.
        navigate(`/intake/capture/${session.id}/words?capture=${session.id}&child=${session.child_id}`);
    };

    return (
        <div className="cap-page">
            <header className="cap-head">
                <div>
                    <h1>Recording session</h1>
                    <p className="cap-sub">
                        {session.child_name} · session {session.id}
                        {session.operator_name ? ` · ${session.operator_name}` : ''}
                    </p>
                </div>
                <span className={`cap-status ${session.status}`}>
                    {session.status === 'complete' ? 'Closed'
                        : session.status === 'in_progress' ? 'In progress' : 'Not started'}
                </span>
            </header>

            {error ? <p className="cap-error">{error}</p> : null}

            {!session.audio_allowed ? (
                <p className="cap-note red">{session.consent_reason}</p>
            ) : null}

            <PreflightCard session={session} protocol={protocol} onSaved={setSession} />

            <div className="cap-card">
                <h3>Blocks</h3>
                <p className="cap-hint">
                    Coverage counts across every session for this child, not just this one —
                    a tired child can stop and finish next time.
                </p>
                <ul className="cap-blocks">
                    {blocks.map((block) => {
                        const state = session.blocks?.[block.key] || 'none';
                        const caseState = session.case_blocks?.[block.key] || 'none';
                        const needsVideo = block.requires_video && !session.video_allowed;
                        const locked = !session.preflight_complete || !session.audio_allowed
                            || session.status === 'complete' || needsVideo;
                        return (
                            <li key={block.key} className={`cap-block ${state}`}>
                                <span className="cap-block-icon" aria-hidden="true">{block.icon}</span>
                                <div className="cap-block-text">
                                    <strong>{block.clinical_name?.en}</strong>
                                    <span className="cap-block-child">
                                        child sees: {block.child_title?.en} · {block.child_title?.hi}
                                    </span>
                                    <span className="cap-block-state">
                                        This session: {BLOCK_STATE_LABEL[state]}
                                        {caseState !== state ? ` · Earlier sessions: ${BLOCK_STATE_LABEL[caseState]}` : ''}
                                    </span>
                                    {needsVideo ? (
                                        <span className="cap-block-reason">
                                            Closed — no camera permission on file.
                                        </span>
                                    ) : null}
                                </div>
                                <button
                                    type="button"
                                    className="cap-btn"
                                    disabled={locked}
                                    onClick={() => (block.key === 'hat' ? startHat() : setActiveBlock(block.key))}
                                >
                                    {state === 'none' ? 'Start' : 'Continue'}
                                </button>
                            </li>
                        );
                    })}
                </ul>
                {!session.preflight_complete ? (
                    <p className="cap-hint">Blocks open once pre-flight is complete.</p>
                ) : null}
            </div>

            <div className="cap-card">
                <h3>Finish</h3>
                <p className="cap-hint">
                    {session.gate_4_satisfied
                        ? 'This child now has enough recorded for the therapist to work from.'
                        : 'The word list and the Fast Talking Game are still needed before the therapist can synthesise — across all sessions, not only this one.'}
                </p>
                <div className="cap-actions">
                    <button
                        type="button" className="cap-btn" onClick={close}
                        disabled={closing || session.status === 'complete'}
                    >
                        {session.status === 'complete' ? 'Session closed' : closing ? 'Closing…' : 'Close this session'}
                    </button>
                    <button type="button" className="cap-btn ghost" onClick={() => navigate('/intake')}>
                        Leave it open
                    </button>
                </div>
            </div>
        </div>
    );
}
